import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');

  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    category: 'Life Coaching',
    ageGroup: '10-17',
    teacher: ''
  });

  const [enrollmentForm, setEnrollmentForm] = useState({
    studentId: '',
    courseId: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, teachersRes, studentsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/courses`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/users/teachers`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/users/students`)
      ]);
      setCourses(coursesRes.data);
      setTeachers(teachersRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      // Remove teacher field if empty
      const courseData = { ...courseForm };
      if (!courseData.teacher) {
        delete courseData.teacher;
      }
      
      await axios.post(`${process.env.REACT_APP_API_URL}/api/courses`, courseData);
      setShowModal(false);
      setCourseForm({ title: '', description: '', category: 'Life Coaching', ageGroup: '10-17', teacher: '' });
      fetchData();
      alert('Course created successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create course');
    }
  };

  const handleEnrollStudent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/enrollments`, enrollmentForm);
      setShowModal(false);
      setEnrollmentForm({ studentId: '', courseId: '' });
      alert('Student enrolled successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to enroll student');
    }
  };

  const handleAssignTeacher = async (courseId, teacherId) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/courses/${courseId}`, { teacher: teacherId });
      fetchData();
      alert('Teacher assigned successfully!');
    } catch (error) {
      alert('Failed to assign teacher');
    }
  };

  const openModal = (type) => {
    setModalType(type);
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-extrabold mb-2">
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              👑 Admin Control Center
            </span>
          </h1>
          <p className="text-gray-600 font-medium">Manage courses, teachers, and students efficiently</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-6 text-white shadow-xl transform hover:scale-105 transition-transform">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 font-semibold">Total Courses</p>
                <p className="text-4xl font-extrabold mt-2">{courses.length}</p>
              </div>
              <div className="text-6xl opacity-50">📚</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl p-6 text-white shadow-xl transform hover:scale-105 transition-transform">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 font-semibold">Total Teachers</p>
                <p className="text-4xl font-extrabold mt-2">{teachers.length}</p>
              </div>
              <div className="text-6xl opacity-50">👨‍🏫</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl p-6 text-white shadow-xl transform hover:scale-105 transition-transform">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 font-semibold">Total Students</p>
                <p className="text-4xl font-extrabold mt-2">{students.length}</p>
              </div>
              <div className="text-6xl opacity-50">🎓</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 bg-white rounded-xl p-2 shadow-md">
          <button
            onClick={() => setActiveTab('courses')}
            className={`flex-1 py-3 px-6 rounded-lg font-bold transition-all ${
              activeTab === 'courses' 
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            📚 Courses
          </button>
          <button
            onClick={() => setActiveTab('teachers')}
            className={`flex-1 py-3 px-6 rounded-lg font-bold transition-all ${
              activeTab === 'teachers' 
                ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            👨‍🏫 Teachers
          </button>
          <button
            onClick={() => setActiveTab('students')}
            className={`flex-1 py-3 px-6 rounded-lg font-bold transition-all ${
              activeTab === 'students' 
                ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white shadow-lg' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            🎓 Students
          </button>
        </div>

        {/* Courses Tab */}
        {activeTab === 'courses' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-extrabold text-gray-900">📚 Course Management</h2>
              <div className="space-x-3">
                <button
                  onClick={() => openModal('course')}
                  className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-xl font-bold hover:from-orange-600 hover:to-red-600 shadow-lg transform hover:-translate-y-1 transition-all"
                >
                  ➕ Create Course
                </button>
                <button
                  onClick={() => openModal('enroll')}
                  className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-6 py-3 rounded-xl font-bold hover:from-green-600 hover:to-teal-600 shadow-lg transform hover:-translate-y-1 transition-all"
                >
                  ✅ Enroll Student
                </button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {courses.map(course => (
                <div key={course._id} className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-orange-500 hover:shadow-xl transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-2xl font-extrabold text-gray-900">{course.title}</h3>
                    <span className="text-3xl">📖</span>
                  </div>
                  <p className="text-gray-600 mb-3 font-medium">{course.description}</p>
                  <div className="space-y-2 mb-4">
                    <p className="text-sm text-gray-700">
                      <span className="font-bold text-orange-600">Category:</span> {course.category}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-bold text-orange-600">Age Group:</span> {course.ageGroup}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t-2 border-gray-100">
                    <span className="text-sm font-semibold text-gray-700">
                      👨‍🏫 {course.teacher?.name || 'Not assigned'}
                    </span>
                    <select
                      onChange={(e) => handleAssignTeacher(course._id, e.target.value)}
                      className="text-sm border-2 border-orange-300 rounded-lg px-3 py-2 font-medium focus:border-orange-500 focus:outline-none"
                      defaultValue=""
                    >
                      <option value="">Assign Teacher</option>
                      {teachers.map(teacher => (
                        <option key={teacher._id} value={teacher._id}>{teacher.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Teachers Tab */}
        {activeTab === 'teachers' && (
          <div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-6">👨‍🏫 Teacher Management</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {teachers.map(teacher => (
                <div key={teacher._id} className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl shadow-lg border-4 border-blue-200 hover:shadow-xl transition-all transform hover:-translate-y-1">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-2xl font-bold text-white shadow-md">
                      {teacher.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-xl font-extrabold text-gray-900">{teacher.name}</h3>
                      <p className="text-sm text-blue-600 font-semibold">⭐ Expert Guru</p>
                    </div>
                  </div>
                  <p className="text-gray-700 font-medium">📧 {teacher.email}</p>
                  <p className="text-sm text-gray-600 mt-2">{teacher.bio || 'No bio available'}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-6">🎓 Student Management</h2>
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-4 border-green-200">
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-green-500 to-teal-500 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-extrabold">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-extrabold">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-extrabold">Age Group</th>
                  </tr>
                </thead>
                <tbody className="divide-y-2 divide-gray-200">
                  {students.map(student => (
                    <tr key={student._id} className="hover:bg-green-50 transition-colors">
                      <td className="px-6 py-4 font-bold text-gray-900">{student.name}</td>
                      <td className="px-6 py-4 text-gray-700">{student.email}</td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                          {student.ageGroup || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modals */}
        {showModal && modalType === 'course' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
              <h2 className="text-2xl font-bold mb-4">Create Course</h2>
              <form onSubmit={handleCreateCourse} className="space-y-4">
                <div>
                  <label className="block text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={courseForm.title}
                    onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">Description</label>
                  <textarea
                    value={courseForm.description}
                    onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    rows="3"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">Category</label>
                  <select
                    value={courseForm.category}
                    onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Life Coaching">Life Coaching</option>
                    <option value="Communication">Communication</option>
                    <option value="Language Learning">Language Learning</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">Age Group</label>
                  <select
                    value={courseForm.ageGroup}
                    onChange={(e) => setCourseForm({ ...courseForm, ageGroup: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="10-17">10-17</option>
                    <option value="17-22">17-22</option>
                  </select>
                </div>
                <div className="flex space-x-4">
                  <button type="submit" className="flex-1 bg-primary text-white py-2 rounded-lg">
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-300 py-2 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showModal && modalType === 'enroll' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
              <h2 className="text-2xl font-bold mb-4">Enroll Student</h2>
              <form onSubmit={handleEnrollStudent} className="space-y-4">
                <div>
                  <label className="block text-gray-700 mb-2">Student</label>
                  <select
                    value={enrollmentForm.studentId}
                    onChange={(e) => setEnrollmentForm({ ...enrollmentForm, studentId: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">Select Student</option>
                    {students.map(student => (
                      <option key={student._id} value={student._id}>{student.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">Course</label>
                  <select
                    value={enrollmentForm.courseId}
                    onChange={(e) => setEnrollmentForm({ ...enrollmentForm, courseId: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">Select Course</option>
                    {courses.map(course => (
                      <option key={course._id} value={course._id}>{course.title}</option>
                    ))}
                  </select>
                </div>
                <div className="flex space-x-4">
                  <button type="submit" className="flex-1 bg-green-500 text-white py-2 rounded-lg">
                    Enroll
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-300 py-2 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
